
import React, { useEffect, useState } from 'react';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "components/ui/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/ui/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { Card, Grid, Icon } from "@mui/material";
import MDButton from "components/MDButton";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { startOfMonth, endOfMonth, format, subDays, addDays } from 'date-fns';
import { request } from "app/network";
import AdDetailDialog from "./components/AdDetailDialog/AdDetailDialog";

import DataTable from "components/ui/Tables/DataTable";

// Register ChartJS
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

interface AdPerformanceStats {
    summary: {
        totalViews: number;
        totalClicks: number;
        avgCtr: number;
        avgCompletionRate: number;
    };
    dailyTrends: {
        date: string;
        dateStr: string;
        views: number;
        clicks: number;
    }[];
    adPerformanceList: {
        rank: number;
        seqId: number;
        title: string;
        views: number;
        clicks: number;
        ctr: number;
        status: string;
        statusLabel: string;
    }[];
}

const SummaryCard = ({ title, value, unit, icon, color }: any) => (
    <Card sx={{ height: "100%", overflow: "hidden", position: "relative" }}>
        <MDBox
            p={3}
            sx={{
                background: color,
                color: "#fff",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
            }}
        >
            <MDBox display="flex" alignItems="center" mb={1}>
                {icon && <Icon style={{ color: "#ffffff" }} sx={{ fontSize: "2rem !important", mr: 1 }}>{icon}</Icon>}
                <MDTypography variant="h6" color="white" fontWeight="medium" sx={{ opacity: 0.9 }}>
                    {title}
                </MDTypography>
            </MDBox>
            <MDTypography variant="h3" fontWeight="bold" color="white">
                {value}
                <MDTypography component="span" variant="h5" color="white" sx={{ ml: 0.5, opacity: 0.8 }}>
                    {unit}
                </MDTypography>
            </MDTypography>
        </MDBox>
    </Card>
);

// Memoized Chart Component to prevent re-renders on scroll
const AdPerformanceChart = React.memo(({ stats }: { stats: AdPerformanceStats | null }) => {
    // Chart Data
    const chartData = React.useMemo(() => ({
        labels: stats?.dailyTrends.map(d => d.dateStr) || [],
        datasets: [
            {
                type: 'bar' as const,
                label: '노출 (Impressions)',
                data: stats?.dailyTrends.map(d => d.views) || [],
                backgroundColor: 'rgba(54, 162, 235, 0.7)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 0,
                borderRadius: 4,
                barPercentage: 0.6,
                yAxisID: 'y',
                order: 2
            },
            {
                type: 'line' as const,
                label: '클릭 (Clicks)',
                data: stats?.dailyTrends.map(d => d.clicks) || [],
                borderColor: '#FF7043',
                backgroundColor: 'rgba(255, 112, 67, 0.2)',
                borderWidth: 3,
                pointRadius: 4,
                pointBackgroundColor: '#fff',
                pointBorderColor: '#FF7043',
                pointHoverRadius: 6,
                fill: true,
                tension: 0.4,
                yAxisID: 'y1',
                order: 1
            },
        ],
    }), [stats]);

    const chartOptions = React.useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        family: 'Inter',
                        size: 12
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                titleFont: { size: 13 },
                bodyFont: { size: 13 },
                displayColors: true,
                intersect: false,
                mode: 'index' as const,
            }
        },
        scales: {
            x: {
                grid: {
                    display: false,
                    drawBorder: false,
                },
                ticks: {
                    font: { size: 12 },
                    color: '#9e9e9e',
                    maxRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 10
                }
            },
            y: {
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                beginAtZero: true,
                grid: {
                    display: true,
                    color: 'rgba(255, 255, 255, 0.1)',
                    borderDash: [5, 5],
                    drawBorder: false,
                },
                ticks: {
                    padding: 10,
                    font: { size: 12 },
                    color: '#9e9e9e'
                },
                title: {
                    display: true,
                    text: '노출',
                    color: '#9e9e9e',
                    font: { size: 10 }
                }
            },
            y1: {
                type: 'linear' as const,
                display: true,
                position: 'right' as const,
                beginAtZero: true,
                grid: {
                    drawOnChartArea: false,
                    drawBorder: false,
                },
                ticks: {
                    padding: 10,
                    font: { size: 12 },
                    color: '#FF7043'
                },
                title: {
                    display: true,
                    text: '클릭',
                    color: '#FF7043',
                    font: { size: 10 }
                }
            },
        },
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
    }), [stats]);

    return <Line data={chartData} options={chartOptions as any} />;
});

function AdStatsScreen() {
    const [stats, setStats] = useState<AdPerformanceStats | null>(null);
    const [loading, setLoading] = useState(false);

    // Default: Current Month
    const [startDate, setStartDate] = useState<Date>(startOfMonth(new Date()));
    const [endDate, setEndDate] = useState<Date>(new Date()); // Today

    // Detail Dialog State
    const [detailAdId, setDetailAdId] = useState<number | null>(null);

    const handleOpenDetail = (seqId: number) => {
        setDetailAdId(seqId);
    };

    const handleCloseDetail = () => {
        setDetailAdId(null);
    };

    const fetchStats = async () => {
        setLoading(true);
        try {
            // Format dates
            const startStr = format(startDate, 'yyyy-MM-dd');
            const endStr = format(endDate, 'yyyy-MM-dd');

            const response = await request("InterstitialAd.getDashboardStats", {
                paramz: {
                    startDate: startStr,
                    endDate: endStr
                }
            });

            if (response && response.data) {
                setStats(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch stats", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, [startDate, endDate]);

    // Table Data
    const tableColumns = [
        { Header: "순위", accessor: "rank", width: "7%" },
        {
            Header: "광고명", accessor: "title", width: "40%",
            Cell: ({ row, value }: any) => (
                <MDTypography
                    variant="caption"
                    fontWeight="medium"
                    color="info"
                    sx={{ cursor: 'pointer', "&:hover": { textDecoration: 'underline' } }}
                    onClick={() => handleOpenDetail(row.original.seqId)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "노출", accessor: "views",
            Cell: ({ value }: any) => <MDTypography variant="caption" fontWeight="medium">{value?.toLocaleString()}</MDTypography>
        },
        {
            Header: "클릭", accessor: "clicks",
            Cell: ({ value }: any) => <MDTypography variant="caption" fontWeight="medium">{value?.toLocaleString()}</MDTypography>
        },
        {
            Header: "CTR (%)", accessor: "ctr",
            Cell: ({ value }: any) => <MDTypography variant="caption" fontWeight="bold" color="text">{value}%</MDTypography>
        },
        {
            Header: "상태", accessor: "status",
            Cell: ({ row }: any) => {
                const status = row.original.status;
                const label = row.original.statusLabel;
                let color: "success" | "warning" | "error" | "secondary" = "secondary";

                if (status === 'Active') color = "success";
                if (status === 'Scheduled') color = "warning";
                if (status === 'Ended') color = "secondary";

                return (
                    <MDBox display="flex" alignItems="center">
                        <MDBox
                            sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                bgcolor: color === 'success' ? '#4CAF50' : (color === 'warning' ? '#FF9800' : '#9E9E9E'),
                                mr: 0.5
                            }}
                        />
                        <MDTypography variant="caption" fontWeight="medium" color={color}>
                            {label}
                        </MDTypography>
                    </MDBox>
                );
            }
        },
    ];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={3} pb={3}>
                {/* Header & Date Picker */}
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <MDTypography variant="h4" fontWeight="bold" color="dark">
                        광고 성과 통계
                    </MDTypography>
                    <MDBox display="flex" gap={1}>
                        <MDButton variant="outlined" color="secondary" size="small" onClick={() => {
                            setStartDate(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)); // Last Month
                            setEndDate(new Date(new Date().getFullYear(), new Date().getMonth(), 0));
                        }}>지난달</MDButton>
                        <MDButton variant="outlined" color="info" size="small" onClick={() => {
                            setStartDate(startOfMonth(new Date())); // This Month
                            setEndDate(new Date());
                        }}>이번달</MDButton>
                        <Card sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', ml: 2, border: '1px solid #e0e0e0' }}>
                            <Icon fontSize="small" sx={{ mr: 1, color: '#7b809a' }}>date_range</Icon>
                            <MDTypography variant="button" fontWeight="medium" color="text">
                                {format(startDate, 'yyyy.MM.dd')} ~ {format(endDate, 'yyyy.MM.dd')}
                            </MDTypography>
                        </Card>
                    </MDBox>
                </MDBox>

                {/* Summary Cards */}
                <MDBox mb={3}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6} lg={3}>
                            <SummaryCard
                                title="총 노출 수"
                                value={stats?.summary.totalViews.toLocaleString() || "0"}
                                unit=""
                                icon="visibility"
                                color="linear-gradient(135deg, #42A5F5 0%, #1E88E5 100%)" // Blue
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <SummaryCard
                                title="총 클릭 수"
                                value={stats?.summary.totalClicks.toLocaleString() || "0"}
                                unit=""
                                icon="touch_app"
                                color="linear-gradient(135deg, #FF8A65 0%, #FF5722 100%)" // Orange
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <SummaryCard
                                title="평균 클릭률"
                                value={stats?.summary.avgCtr || "0"}
                                unit="%"
                                icon="analytics"
                                color="linear-gradient(135deg, #66BB6A 0%, #43A047 100%)" // Green
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <SummaryCard
                                title="광고 완료율"
                                value={stats?.summary.avgCompletionRate || "0"}
                                unit="%"
                                icon="play_circle_filled"
                                color="linear-gradient(135deg, #AB47BC 0%, #8E24AA 100%)" // Purple
                            />
                        </Grid>
                    </Grid>
                </MDBox>

                {/* Chart Section */}
                <MDBox mb={3}>
                    <Card sx={{ height: "100%" }}>
                        <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                            <MDTypography variant="h6" fontWeight="bold">
                                일별 광고 성과
                            </MDTypography>
                        </MDBox>
                        <MDBox p={3} pt={0} sx={{ height: "400px" }}>
                            {loading ? (
                                <MDTypography variant="button" display="flex" justifyContent="center" alignItems="center" height="100%">
                                    Loading Chart...
                                </MDTypography>
                            ) : (
                                <AdPerformanceChart stats={stats} />
                            )}
                        </MDBox>
                    </Card>
                </MDBox>


                {/* List Section */}
                <Card>
                    <MDBox p={3}>
                        <MDTypography variant="h6" fontWeight="bold">
                            광고별 성과 리스트
                        </MDTypography>
                    </MDBox>
                    <DataTable
                        table={{ columns: tableColumns, rows: stats?.adPerformanceList || [] }}
                        showTotalEntries={false}
                        isSorted={false}
                        entriesPerPage={false}
                    />
                </Card>

            </MDBox>
            <Footer />
            <AdDetailDialog
                open={!!detailAdId}
                onClose={handleCloseDetail}
                seqId={detailAdId || 0}
                startDate={startDate}
                endDate={endDate}
            />
        </DashboardLayout>
    );
}

export default AdStatsScreen;
